import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import type React from 'react';

// ── Types matching Phase 1 API response ──────────────────────────────────────

export interface CalendarTrade {
  decisionSK?: string;
  symbol: string;
  direction: 'long' | 'short';
  entryPrice: number;
  exitPrice: number;
  pnl: number;
  pnlPct: number;
  qty: number;
  strategy: string;
  closedAt: string;
  reasoning: string;
}

export interface CalendarDay {
  date: string; // "YYYY-MM-DD"
  netPnl: number;
  tradeCount: number;
  winCount: number;
  lossCount: number;
  trades: CalendarTrade[];
}

export interface CalendarResponse {
  days: CalendarDay[];
  startDate: string;
  endDate: string;
}

export interface DayTradeSummary {
  totalPnl: number;
  tradeCount: number;
  winCount: number;
  lossCount: number;
  winRate: number;
  bestTrade: CalendarTrade | null;
  worstTrade: CalendarTrade | null;
  topStrategy: string | null;
}

// ── Grid Math ────────────────────────────────────────────────────────────────

/**
 * Returns an array of dates covering the full Sun-Sat calendar grid for
 * the given month. Always starts on Sunday and ends on Saturday,
 * producing either 35 (5 weeks) or 42 (6 weeks) dates.
 */
export function getCalendarDays(month: Date): Date[] {
  const start = startOfWeek(startOfMonth(month));
  const end = endOfWeek(endOfMonth(month));
  return eachDayOfInterval({ start, end });
}

// ── Percentile Normalization ─────────────────────────────────────────────────

/**
 * Maps an array of absolute P&L values to percentile ranks (0 to 1).
 * Uses rank-based normalization to prevent outlier washout.
 *
 * - Empty array -> empty Map
 * - Single value -> Map with that value -> 0
 * - Duplicate values receive the same (first-occurrence) rank
 */
export function computePercentileRanks(pnlValues: number[]): Map<number, number> {
  const ranks = new Map<number, number>();
  if (pnlValues.length === 0) return ranks;

  const sorted = [...pnlValues].sort((a, b) => a - b);
  const denominator = Math.max(sorted.length - 1, 1);

  for (let i = 0; i < sorted.length; i++) {
    const val = sorted[i];
    // First occurrence wins -- duplicates get same rank
    if (!ranks.has(val)) {
      ranks.set(val, i / denominator);
    }
  }

  return ranks;
}

// ── Color Scale ──────────────────────────────────────────────────────────────

/**
 * Returns inline CSS style for a calendar day cell based on P&L direction
 * and percentile intensity.
 *
 * - Positive P&L -> green rgba(39, 174, 96, alpha)
 * - Negative P&L -> red rgba(192, 57, 43, alpha)
 * - Zero P&L -> empty object (no background)
 * - Alpha range: 0.12 (subtle) to 0.70 (intense)
 */
export function pnlCellStyle(pnl: number, percentile: number): React.CSSProperties {
  if (pnl === 0) return {};

  const alpha = 0.12 + percentile * 0.58;

  if (pnl > 0) {
    return { backgroundColor: `rgba(39, 174, 96, ${alpha})` };
  }

  return { backgroundColor: `rgba(192, 57, 43, ${alpha})` };
}

// ── Monthly Stats ───────────────────────────────────────────────────────────

export interface StreakInfo {
  count: number;
  type: 'win' | 'loss';
}

export interface DayHighlight {
  date: string;
  netPnl: number;
}

export interface MonthlyStats {
  totalPnl: number;
  tradeCount: number;
  winDays: number;
  lossDays: number;
  currentStreak: StreakInfo;
  longestStreak: StreakInfo;
  bestDay: DayHighlight | null;
  worstDay: DayHighlight | null;
}

/**
 * Computes monthly summary statistics from an array of CalendarDay records.
 * Days with tradeCount === 0 are excluded from win/loss/streak/best/worst
 * calculations but their tradeCount still contributes to the total.
 * Zero P&L trading days are neutral — they break streaks and don't count as win or loss.
 */
export function computeMonthlyStats(days: CalendarDay[]): MonthlyStats {
  const totalPnl = days.reduce((sum, d) => sum + d.netPnl, 0);
  const tradeCount = days.reduce((sum, d) => sum + d.tradeCount, 0);

  // Filter to trading days only (tradeCount > 0) and sort chronologically
  const tradingDays = [...days].filter((d) => d.tradeCount > 0).sort((a, b) => a.date.localeCompare(b.date));

  let winDays = 0;
  let lossDays = 0;
  let bestDay: DayHighlight | null = null;
  let worstDay: DayHighlight | null = null;

  for (const d of tradingDays) {
    if (d.netPnl > 0) winDays++;
    if (d.netPnl < 0) lossDays++;

    if (bestDay === null || d.netPnl > bestDay.netPnl) {
      bestDay = { date: d.date, netPnl: d.netPnl };
    }
    if (worstDay === null || d.netPnl < worstDay.netPnl) {
      worstDay = { date: d.date, netPnl: d.netPnl };
    }
  }

  // Compute streaks — zero P&L days break streaks
  let currentStreak: StreakInfo = { count: 0, type: 'win' };
  let longestStreak: StreakInfo = { count: 0, type: 'win' };
  let runCount = 0;
  let runType: 'win' | 'loss' | null = null;

  for (const d of tradingDays) {
    const sign: 'win' | 'loss' | 'neutral' = d.netPnl > 0 ? 'win' : d.netPnl < 0 ? 'loss' : 'neutral';

    if (sign === 'neutral') {
      // Neutral breaks the streak
      if (runCount > 0 && runType !== null && runCount > longestStreak.count) {
        longestStreak = { count: runCount, type: runType };
      }
      runCount = 0;
      runType = null;
    } else if (sign === runType) {
      runCount++;
    } else {
      // New sign — save previous run if longer
      if (runCount > 0 && runType !== null && runCount > longestStreak.count) {
        longestStreak = { count: runCount, type: runType };
      }
      runCount = 1;
      runType = sign;
    }
  }

  // Final run check
  if (runCount > 0 && runType !== null) {
    if (runCount > longestStreak.count) {
      longestStreak = { count: runCount, type: runType };
    }
    currentStreak = { count: runCount, type: runType };
  }

  return {
    totalPnl,
    tradeCount,
    winDays,
    lossDays,
    currentStreak,
    longestStreak,
    bestDay,
    worstDay,
  };
}

export function computeDayTradeSummary(trades: CalendarTrade[]): DayTradeSummary {
  const strategyCounts = new Map<string, number>();

  return trades.reduce<DayTradeSummary>(
    (summary, trade) => {
      const totalPnl = summary.totalPnl + trade.pnl;
      const winCount = summary.winCount + (trade.pnl > 0 ? 1 : 0);
      const lossCount = summary.lossCount + (trade.pnl < 0 ? 1 : 0);
      const tradeCount = summary.tradeCount + 1;
      const bestTrade = !summary.bestTrade || trade.pnl > summary.bestTrade.pnl ? trade : summary.bestTrade;
      const worstTrade = !summary.worstTrade || trade.pnl < summary.worstTrade.pnl ? trade : summary.worstTrade;

      if (trade.strategy) {
        strategyCounts.set(trade.strategy, (strategyCounts.get(trade.strategy) ?? 0) + 1);
      }

      let topStrategy = summary.topStrategy;
      strategyCounts.forEach((count, strategy) => {
        if (!topStrategy || count > (strategyCounts.get(topStrategy) ?? 0)) {
          topStrategy = strategy;
        }
      });

      return {
        totalPnl,
        tradeCount,
        winCount,
        lossCount,
        winRate: tradeCount > 0 ? Math.round((winCount / tradeCount) * 100) : 0,
        bestTrade,
        worstTrade,
        topStrategy,
      };
    },
    {
      totalPnl: 0,
      tradeCount: 0,
      winCount: 0,
      lossCount: 0,
      winRate: 0,
      bestTrade: null,
      worstTrade: null,
      topStrategy: null,
    },
  );
}
